import type { Dispatch } from '@reduxjs/toolkit';
import type { AppleUser } from 'react-apple-signin-auth';

import { userApi } from '@/entities/user/@x/session';
import { parseApiError } from '@/shared/api/apiError';
import { baseApi } from '@/shared/api/baseApi';
import { cacheBaseKeys } from '@/shared/api/cacheTags';
import {
  SIGNED_IN_ELSEWHERE_TEXT,
  sessionEnded,
} from '@/shared/api/sessionEvents';
import type { AuthResponse, SocialType, User } from '@/shared/api/types';
import { logout as logoutNav } from '@/shared/model/navSlice';

import {
  clearState,
  logout,
  setAuthCredentials,
  setAuthSocialCreds,
  setTokens,
  setValidRefreshToken,
} from '../model/authSlice';
import type { AuthState } from '../model/authSlice';

type Params = Record<string, unknown>;

const serialize = (obj: Params, prefix?: string): string => {
  const str: string[] = [];

  for (const p in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, p)) {
      const key = prefix ?? p;
      const value = obj[p];

      str.push(
        value !== null && typeof value === 'object'
          ? serialize(value as Params, key)
          : `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`,
      );
    }
  }

  return str.join('&');
};

// Body of `POST auth/confirm-email/`: the values of the sign-up form.
// The add-email form and the "link expired" page send the email alone.
export interface SignUpRequest {
  email: string;
  first_name?: string;
  last_name?: string;
  accept?: boolean;
  confirm?: string;
  // The address being corrected on the check-your-mailbox page: its link stops working
  previous_email?: string;
}

// What `GET auth/confirm-email/?code=` says about a sign-up confirmation or invitation link.
// Opening the link activates nothing; the password is set with `setPasswordByCode`.
export interface ConfirmLinkInfo {
  kind: 'signup' | 'invitation';
  email: string;
  first_name: string | null;
  last_name: string | null;
}

// Error codes of a link that can't be used (backend `code_already_used` / `code_expired_or_invalid`)
export const LINK_ALREADY_USED = 'code_already_used';
export const LINK_EXPIRED = 'code_expired_or_invalid';

// Clears the session on this device only
export const logoutUser = () => (dispatch: Dispatch) => {
  dispatch(logout());
  dispatch(logoutNav());
  dispatch(baseApi.util.resetApiState());
};

// Sign out by the user: ends the session on the server for good (AU-6-05), then on this device.
// The request carries the refresh token, so it works with an expired access token too. It is a
// plain `keepalive` fetch rather than an endpoint: clearing the API state right after would
// cancel a running mutation, and the page may navigate away before it completes.
export const signOut =
  () => (dispatch: AppDispatch, getState: () => RootState) => {
    const { refreshToken } = getState().auth;

    if (refreshToken) {
      fetch(`${import.meta.env.VITE_BASE_API_ROUTE}/auth/logout/`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      }).catch(() => {});
    }
    dispatch(logoutUser());
  };

const storeSession = (
  dispatch: AppDispatch,
  data: AuthResponse,
  remember: boolean,
) => {
  dispatch(
    setAuthCredentials({
      userInfo: data.user,
      accessToken: data.access,
      refreshToken: data.refresh,
      remember,
    }),
  );
  dispatch(
    userApi.util.updateQueryData('getUserInfo', undefined, (draft) => {
      Object.assign(draft, data.user);
    }),
  );
  dispatch(setValidRefreshToken(true));
};

const authApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    signIn: builder.mutation<
      AuthResponse,
      { email: string; password: string; remember?: boolean }
    >({
      query: ({ email, password, remember }) => ({
        url: 'auth/token/',
        method: 'POST',
        // `remember_me` gives a longer session on the server (AU-6-03)
        body: { email, password, remember_me: !!remember },
        encrypt: true,
      }),
      async onQueryStarted({ remember }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          storeSession(dispatch, data, !!remember);
        } catch (_error) {}
      },
    }),

    // The sign-up confirmation or invitation link: whose account it is. Doesn't sign in.
    checkConfirmLink: builder.mutation<ConfirmLinkInfo, { code: string }>({
      query: ({ code }) => ({
        url: `auth/confirm-email/?${serialize({ code })}`,
        method: 'GET',
      }),
    }),

    // The password step of a sign up and the invitation page: sets the password,
    // activates the account and signs in
    setPasswordByCode: builder.mutation<
      AuthResponse,
      { code: string; password: string }
    >({
      query: (body) => ({
        url: 'auth/confirm-email/password/',
        method: 'POST',
        body,
        encrypt: true,
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(clearState());
          storeSession(dispatch, data, false);
        } catch (_error) {}
      },
    }),

    socialAuth: builder.mutation<
      AuthResponse,
      // `user` is what Apple sends along with the first sign-in
      {
        access_token: string;
        socialType: SocialType;
        user?: AppleUser;
        remember?: boolean;
      }
    >({
      query: ({ access_token, socialType, user }) => ({
        url: `auth/social/${socialType}/`,
        method: 'POST',
        body: {
          access_token,
          ...(user && { user }),
        },
      }),
      async onQueryStarted({ remember }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            setAuthSocialCreds({
              userInfo: data.user,
              accessToken: data.access,
              refreshToken: data.refresh,
              remember: !!remember,
            }),
          );
          dispatch(
            userApi.util.updateQueryData('getUserInfo', undefined, (draft) => {
              Object.assign(draft, data.user);
            }),
          );
          dispatch(setValidRefreshToken(true));
        } catch (_error) {}
      },
    }),

    // Without a token it signs a user up, with a token it changes the email during onboarding
    checkEmail: builder.mutation<string, SignUpRequest>({
      query: (data) => ({
        url: 'auth/confirm-email/',
        method: 'POST',
        body: data,
        encrypt: true,
      }),
    }),

    confirmEmail: builder.mutation<AuthResponse, { hash: string }>({
      query: ({ hash }) => ({
        url: `auth/confirm-email/?${serialize({ code: hash })}`,
        method: 'GET',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        dispatch(clearState());

        try {
          const { data } = await queryFulfilled;

          dispatch(
            setAuthCredentials({
              userInfo: data.user,
              accessToken: data.access,
              refreshToken: data.refresh,
            }),
          );
          dispatch(
            userApi.util.updateQueryData('getUserInfo', undefined, (draft) => {
              Object.assign(draft, data.user);
            }),
          );
          dispatch(setValidRefreshToken(true));
        } catch (_error) {}
      },
    }),

    checkPassword: builder.mutation<
      unknown,
      { email: string; redirect?: boolean }
    >({
      query: ({ email }) => ({
        url: 'auth/forgot-password/',
        method: 'POST',
        body: { email },
        encrypt: true,
      }),
    }),

    // A signed-out user has to send the email along with the code
    confirmPassword: builder.mutation<
      Partial<AuthResponse>,
      { code: string; email?: string }
    >({
      query: ({ code, email }) => ({
        url: `auth/forgot-password/?${serialize(email ? { code, email } : { code })}`,
        method: 'GET',
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          if (data?.access && data?.refresh && data?.user) {
            dispatch(
              setAuthCredentials({
                userInfo: data.user,
                accessToken: data.access,
                refreshToken: data.refresh,
              }),
            );
            dispatch(
              userApi.util.updateQueryData(
                'getUserInfo',
                undefined,
                (draft) => {
                  Object.assign(draft, data.user);
                },
              ),
            );
            dispatch(setValidRefreshToken(true));
          }
        } catch (_error) {}
      },
    }),

    createUserPassword: builder.mutation<
      { message: { detail: string }; onboarding_finished: boolean },
      { password: string; isNewUserCheck?: boolean }
    >({
      query: ({ password }) => ({
        url: '/user/set_password/',
        method: 'POST',
        body: { new_password: password },
        encrypt: true,
      }),
      async onQueryStarted({ isNewUserCheck }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          if (isNewUserCheck) {
            // The answer has no user in it, so the current user is refetched
            dispatch(userApi.util.invalidateTags([cacheBaseKeys.user]));
          } else {
            dispatch(logout());
          }
        } catch (_error) {}
      },
    }),

    socialAuthDelete: builder.mutation<User, { socialType: string }>({
      query: ({ socialType }) => ({
        url: `auth/social/${socialType}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [cacheBaseKeys.user],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          // The answer is the user itself
          dispatch(setAuthCredentials({ userInfo: data }));
          dispatch(
            userApi.util.updateQueryData('getUserInfo', undefined, (draft) => {
              Object.assign(draft, data);
            }),
          );
        } catch (_error) {}
      },
    }),

    refreshToken: builder.mutation<AuthResponse, { token: string }>({
      query: ({ token }) => ({
        url: 'auth/token/refresh/',
        method: 'POST',
        body: { refresh: token },
      }),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          dispatch(
            setTokens({
              accessToken: data.access,
              refreshToken: data.refresh,
            }),
          );
          dispatch(setValidRefreshToken(true));
        } catch (error) {
          dispatch(setValidRefreshToken(false));
          dispatch(logoutUser());
          // The session checked on opening the app was replaced by a sign in elsewhere (AU-6-02)
          const apiError = parseApiError(
            (error as { error?: unknown })?.error ?? error,
          );
          if (apiError?.message.includes(SIGNED_IN_ELSEWHERE_TEXT)) {
            dispatch(sessionEnded(apiError.message));
          }
        }
      },
    }),

    verifyToken: builder.mutation<unknown, { token: string }>({
      query: ({ token }) => ({
        url: 'auth/token/verify/',
        method: 'POST',
        body: { token },
      }),
      async onQueryStarted(_arg, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(setValidRefreshToken(true));
        } catch (_error) {
          const refreshToken = (getState() as unknown as { auth: AuthState })
            .auth.refreshToken;

          if (refreshToken) {
            dispatch(
              authApi.endpoints.refreshToken.initiate({ token: refreshToken }),
            );
          } else {
            dispatch(setValidRefreshToken(false));
          }
        }
      },
    }),
  }),
});

export const {
  useCheckConfirmLinkMutation,
  useSetPasswordByCodeMutation,
  useSignInMutation,
  useSocialAuthMutation,
  useCheckEmailMutation,
  useConfirmEmailMutation,
  useCheckPasswordMutation,
  useConfirmPasswordMutation,
  useCreateUserPasswordMutation,
  useSocialAuthDeleteMutation,
  useVerifyTokenMutation,
} = authApi;
